/**
 * PrivateLabelCard Component
 * Display card for user-submitted private labels
 */
import React, { useState } from 'react';
import { Mail, Globe, Tag, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PrivateLabelCard = ({ label, currentUser, onEdit, onDelete, onRefresh }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isOwner = currentUser && (
    currentUser.id === label.user_id || 
    currentUser.user_id === label.user_id ||
    currentUser.id === label.created_by ||
    currentUser.user_id === label.created_by
  );

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${label.name}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/api/private-labels/${label.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        onRefresh && onRefresh();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete label');
      }
    } catch (error) {
      console.error('Error deleting label:', error);
      alert('Failed to delete label');
    }
  };

  return (
    <div
      data-testid={`private-label-card-${label.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: isHovered 
          ? 'rgba(40, 30, 60, 0.9)'
          : 'rgba(30, 20, 50, 0.7)',
        borderRadius: '12px',
        border: label.accepting_demos
          ? (isHovered ? '2px solid rgba(34, 197, 94, 0.6)' : '2px solid rgba(34, 197, 94, 0.3)')
          : (isHovered ? '2px solid rgba(147, 51, 234, 0.5)' : '2px solid rgba(147, 51, 234, 0.15)'),
        padding: '1.25rem',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={{ display: 'flex', gap: '1rem' }}>
        {/* Logo */}
        {label.logo_url ? (
          <img 
            src={label.logo_url} 
            alt={label.name}
            style={{ 
              width: '60px', 
              height: '60px', 
              borderRadius: '8px',
              objectFit: 'cover'
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '8px',
            background: 'rgba(147, 51, 234, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Tag size={24} style={{ color: '#a78bfa' }} />
          </div>
        )}

        {/* Info */}
        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#fff', margin: '0 0 0.25rem 0' }}>
            {label.name}
          </h4>
          
          {/* Accepting Demos Badge */}
          {label.accepting_demos && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.15rem 0.4rem',
              borderRadius: '4px',
              background: 'rgba(34, 197, 94, 0.2)',
              color: '#22c55e',
              fontSize: '0.65rem',
              fontWeight: '500',
              marginBottom: '0.5rem'
            }}>
              <CheckCircle size={10} />
              Accepting Demos
            </div>
          )}
          
          {/* Genres */}
          {label.genres && label.genres.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem' }}>
              {label.genres.slice(0, 3).map((genre, idx) => (
                <span
                  key={idx}
                  style={{
                    padding: '0.1rem 0.4rem',
                    borderRadius: '4px',
                    background: 'rgba(147, 51, 234, 0.2)',
                    color: '#a78bfa',
                    fontSize: '0.65rem'
                  }}
                >
                  {genre}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {label.description && (
        <p style={{ 
          fontSize: '0.8rem', 
          color: 'rgba(255, 255, 255, 0.6)',
          margin: '0.75rem 0',
          lineHeight: '1.4'
        }}>
          {label.description}
        </p>
      )}

      {/* Contact & Links */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
        {label.email && (
          <a
            href={`mailto:${label.email}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              padding: '0.4rem 0.75rem',
              borderRadius: '6px',
              background: 'rgba(147, 51, 234, 0.15)',
              border: '1px solid rgba(147, 51, 234, 0.3)',
              color: '#a78bfa',
              textDecoration: 'none',
              fontSize: '0.75rem'
            }}
          >
            <Mail size={12} />
            Contact
          </a>
        )}
        {label.website && (
          <a
            href={label.website}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              padding: '0.4rem 0.75rem',
              borderRadius: '6px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#fff',
              textDecoration: 'none',
              fontSize: '0.75rem'
            }}
          >
            <Globe size={12} />
            Website
          </a>
        )}
        {label.socials && Object.entries(label.socials).map(([platform, url]) => (
          url && (
            <a
              key={platform}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '0.4rem 0.6rem',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: 'rgba(255, 255, 255, 0.7)',
                textDecoration: 'none',
                fontSize: '0.7rem',
                textTransform: 'capitalize'
              }}
            >
              {platform}
            </a>
          )
        ))}
      </div>

      {/* Owner Actions - Edit & Delete (bottom right) */}
      {isOwner && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '0.75rem',
          marginTop: '0.75rem',
          paddingTop: '0.5rem',
          borderTop: '1px solid rgba(147, 51, 234, 0.15)'
        }}>
          <span
            data-testid={`edit-label-${label.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onEdit && onEdit();
            }}
            style={{
              color: '#fbbf24',
              cursor: 'pointer',
              fontSize: '0.7rem',
              fontWeight: '500'
            }}
          >
            ✏️ Edit
          </span>
          <span
            data-testid={`delete-label-${label.id}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            style={{
              color: '#ef4444',
              cursor: 'pointer',
              fontSize: '0.7rem',
              fontWeight: '500'
            }}
          >
            🗑️ Delete
          </span>
        </div>
      )}
    </div>
  );
};

export default PrivateLabelCard;
